import React, { useRef } from "react";
import * as _ from "lodash";
import { CoordinateSystem } from "./CoordinateSystem";
import {
  cachedEckeEpsilon,
  createEcke,
  createKante,
  findNearestUnder,
  gerichterZuUngerichtemGraph,
  GrundrissEcke,
  GrundrissKante,
  kantenZuEcken,
  kantenZuStrecken,
  removeFromStrecken,
  Strecke2,
  ungerichterZuGerichterGraph,
} from "../../arrangement/grundrissFunctions";
import { findTorAt, onDragEndTor, onDragMoveTor, onDragStartTor, TorState } from "./TorDragging";
import { zoomIn, zoomOut } from "./Zoom";
import { ZElement } from "../../arrangement/ZElement";
import { createGEcke } from "../../arrangement/grundrissGerichteterGraph";
import { v2, v3 } from "oc-threejs/short3";
import * as Maths2 from "oc-threejs/Maths2";
import { distanceToLineSegment, Ray2, uniquePoints } from "oc-threejs/Maths2";
import * as Maths3 from "oc-threejs/Maths3";
import { Box2, Vector2, Vector3 } from "three";
import { KeyState } from "oc-interaction/KeyState";
import { useLocalStorage } from "oc-ui/react/useLocalStorage";
import * as Dialogs from "oc-ui/dialogs/Dialogs";

interface UseGrundrissEventsParams {
  cs: CoordinateSystem;
  state: any;
  setNewState: (newState: any) => void;
  setPointerDown: (value: boolean) => void;
  setSelected: (value: any) => void;
  forceUpdate: () => void;
  setDollyDirty: () => void;
  selectionRectangleRef: React.MutableRefObject<HTMLDivElement | null>;
  torStateRef: React.MutableRefObject<TorState | null>;
}

export function useGrundrissEvents(params: UseGrundrissEventsParams) {
  const {
    cs,
    state,
    setNewState,
    setPointerDown,
    setSelected,
    forceUpdate,
    setDollyDirty,
    selectionRectangleRef,
    torStateRef,
  } = params;

  // Refs für Gestensteuerung
  const gestureWasPinchRef = useRef<boolean>(false);
  const activePointersRef = useRef<Map<number, Vector2>>(new Map());
  const initialCameraRef = useRef<Vector3 | null>(null);
  const initialTouchPointsRef = useRef<Strecke2 | null>(null);
  const isDragRef = useRef<boolean>(false);
  const neueKanteRef = useRef<GrundrissKante | null>(null);
  const verschiebenFunctionRef = useRef<((pointerState: string) => void) | null>(null);
  const modusBeforeRef = useRef<string | null>(null);
  const snapFunctionRef = useRef<((p3: Vector3) => Vector3) | null>(null);
  const verschiebenTargetRef = useRef<{ type: string; punkt?: Vector2; strecke?: any } | null>(
    null
  );
  const pointerDownStartRef = useRef<number | null>(null);
  const moveStartRef = useRef<Vector2 | null>(null);
  const moveEndRef = useRef<Vector2 | null>(null);
  const isPinchingRef = useRef<boolean>(false);
  const [eckVerschiebenInfoAngezeigt, setEckVerschiebenInfoAngezeigt] = useLocalStorage(
    "eckVerschiebenInfoAngezeigt",
    false
  );

  const handleClick = (e: React.MouseEvent) => {
    if (!isPinchingRef.current && !isDragRef.current) {
      const part = getClickedPart(e, cs);
      if (part) {
        setSelected(part);
        return;
      }
    }
    if ((e.target as HTMLElement).classList.contains("aufbau")) {
      return;
    }
    if (!isPinchingRef.current && !isDragRef.current) {
      setSelected(null);
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!cs) return;
    const pointerPos = v2(e.clientX, e.clientY);
    if (e.deltaY !== 0) {
      if (e.deltaY < 0) {
        zoomOut(pointerPos);
      } else {
        zoomIn(pointerPos);
      }
      APP.logUserAction({ action: "wheelZoom" }, { cached: true, stateChange: false });
    }
    setDollyDirty();
  };

  const startVerschiebeTor = (startV2: Vector2, tor: ZElement) => {
    if (!cs) return;
    onDragStartTor(cs, startV2, tor, torStateRef, forceUpdate);
    verschiebenTargetRef.current = null;
    snapFunctionRef.current = null;
    verschiebenFunctionRef.current = (pointerState: string) => {
      const endV2 = Maths3.v3To2xz(cs.pointToGrundriss(moveEndRef.current, 0));
      if (pointerState === "up") {
        onDragEndTor(cs, endV2, torStateRef, forceUpdate);
      } else {
        onDragMoveTor(cs, endV2, torStateRef, forceUpdate);
      }
    };
  };

  const createSnapFunction = () => {
    if (!cs) return null;
    const grundriss = APP.arrangement.grundriss;
    const eckpunkte = uniquePoints(grundriss.ecken.map((e) => e.punkt)).filter(
      (ecke: Vector2) =>
        verschiebenTargetRef.current?.type !== "ecke" ||
        verschiebenTargetRef.current.punkt.distanceTo(ecke) > 0.001
    );
    const rays = kantenZuStrecken(grundriss.kanten)
      .filter(
        (strecke) =>
          verschiebenTargetRef.current?.type !== "strecke" ||
          verschiebenTargetRef.current.strecke.some((p, i) => p.distanceTo(strecke[i]) > 0.001)
      )
      .map((s) => new Ray2().setFromPoints(s));
    let snapDistance = cs.zoomUnitInM / 10;
    while (cs.lengthToScreen(snapDistance) > 40) {
      snapDistance = snapDistance / 2;
    }
    const widerSnapDistance = snapDistance;

    const findClosestEckpunkt = (point: Vector2, distance: number) => {
      const eckpunkteSnaps = eckpunkte
        .map((e: Vector2) => [e, e.distanceTo(point)] as [Vector2, number])
        .filter(([e, d]) => d < distance);
      if (eckpunkteSnaps.length > 0) {
        const [eckpunkt, distance] = _.minBy(eckpunkteSnaps, ([e, d]) => d);
        return eckpunkt;
      }
      return null;
    };

    const uniqNumbers = (numbers: number[]) => _.sortedUniq(numbers.sort((a, b) => a - b));
    const xs = uniqNumbers(eckpunkte.map((p) => p.x));
    const ys = uniqNumbers(eckpunkte.map((p) => p.y));

    const snapAxis = (ns: number[], n: number) => {
      if (KeyState.isCtrl()) {
        const p = ns.find((p) => Math.abs(n - p) < snapDistance);
        if (p !== undefined) {
          return p;
        }
        return n;
      }
      return Math.round(n / snapDistance) * snapDistance;
    };

    return (p3: Vector3) => {
      let p = Maths3.v3To2xz(p3);
      const eckpunkt = findClosestEckpunkt(
        p,
        KeyState.isShift() ? snapDistance : widerSnapDistance
      );
      if (eckpunkt) {
        return Maths3.v2To3xz(eckpunkt);
      }
      if (KeyState.isShift()) {
        return p3;
      }
      const linePoint = findClosestGridLinePoint(rays, widerSnapDistance, snapDistance, p);
      if (linePoint) {
        return Maths3.v2To3xz(linePoint);
      }
      return v3(snapAxis(xs, p.x), 0, snapAxis(ys, p.y));
    };
  };

  const maybeShowVerschiebenSnappToast = () => {
    if (!eckVerschiebenInfoAngezeigt) {
      Dialogs.showToast(
        I18N("Tipp: [CTRL] gedrückt halten für Winkel-Snapping, [SHIFT] drücken für kein Snapping")
      );
      setEckVerschiebenInfoAngezeigt(true);
    }
  };

  // Funktionen für die Modi

  const verschieben = (pointerState: string) => {
    if (!cs) return;
    if (pointerState === "down") {
      const start = cs.pointToGrundriss(moveStartRef.current, 0);
      const grundriss = APP.arrangement.grundriss;
      const ecken = ungerichterZuGerichterGraph(grundriss.ecken);
      const startV2 = Maths3.v3To2xz(start);
      const minDistance = cs.lengthToGrundriss(20);
      const selectierteEcke = findNearestUnder(
        ecken,
        (ecke) => ecke.punkt.distanceTo(startV2),
        minDistance
      );
      if (selectierteEcke) {
        verschiebenTargetRef.current = { type: "ecke", punkt: selectierteEcke.punkt };
        // Ecke verschieben
        verschiebenFunctionRef.current = (pointerState: string) => {
          const start = snapFunctionRef.current(cs.pointToGrundriss(moveStartRef.current, 0));
          const end = snapFunctionRef.current(cs.pointToGrundriss(moveEndRef.current, 0));
          const delta = start.clone().sub(end);
          selectierteEcke.punkt.copy(Maths3.v3To2xz(end));
          ({ ecken: grundriss.ecken, kanten: grundriss.kanten } =
            gerichterZuUngerichtemGraph(ecken));
          if (pointerState == "up" && delta.length() > 0.001) {
            APP.logUserAction({ action: "verschieben" }, { cached: true });
          }
        };
        maybeShowVerschiebenSnappToast();
      } else {
        const tor = findTorAt(startV2, cs);
        if (tor) {
          startVerschiebeTor(startV2, tor);
        } else {
          const strecke = findNearestUnder(
            kantenZuStrecken(grundriss.kanten),
            (strecke) => Maths2.distanceToLineSegment(startV2, strecke),
            minDistance
          );
          if (strecke) {
            verschiebenTargetRef.current = { type: "strecke", strecke: strecke };
            const cachedEcke = cachedEckeEpsilon(ecken, (p) => createGEcke(Commons.createId(), p));
            const eckenDerStrecke = strecke.map(cachedEcke);
            // Strecke Verschieben
            verschiebenFunctionRef.current = (pointerState: string) => {
              const start = snapFunctionRef.current(cs.pointToGrundriss(moveStartRef.current, 0));
              const end = snapFunctionRef.current(cs.pointToGrundriss(moveEndRef.current, 0));
              const delta = end.clone().sub(start);
              eckenDerStrecke.forEach((ecke, i) =>
                ecke.punkt.copy(strecke[i]).add(Maths3.v3To2xz(delta))
              );
              ({ ecken: grundriss.ecken, kanten: grundriss.kanten } =
                gerichterZuUngerichtemGraph(ecken));
              if (pointerState == "up" && delta.length() > 0.001) {
                APP.logUserAction({ action: "verschieben" }, { cached: true });
              }
            };
          } else {
            // Viewport verschieben
            verschiebenFunctionRef.current = (pointerState: string) => {
              const mdelta = moveStartRef.current.clone().sub(moveEndRef.current);
              const start = cs.pointToGrundriss(moveStartRef.current, 0);
              const end = cs.pointToGrundriss(moveEndRef.current, 0);
              const delta = start.clone().sub(end);
              moveStartRef.current = moveEndRef.current;

              // Begrenzung der Viewport-Verschiebung wenn Objekte vorhanden sind
              if (grundriss.ecken.length > 0 || APP.arrangement.aufbauten.length > 0) {
                const puffer = 10; // 10 Meter Puffer um den Grundriss
                const boundingBox = grundriss.boundingBox.clone();
                boundingBox.expandByScalar(puffer);

                const newPosition = APP.controls.object.position.clone().add(delta);
                const newTarget = APP.controls.target.clone().add(delta);

                // Begrenze die X- und Z-Koordinaten auf die erweiterte BoundingBox
                newPosition.x = Math.max(
                  boundingBox.min.x,
                  Math.min(boundingBox.max.x, newPosition.x)
                );
                newPosition.z = Math.max(
                  boundingBox.min.z,
                  Math.min(boundingBox.max.z, newPosition.z)
                );
                newTarget.x = Math.max(boundingBox.min.x, Math.min(boundingBox.max.x, newTarget.x));
                newTarget.z = Math.max(boundingBox.min.z, Math.min(boundingBox.max.z, newTarget.z));

                APP.controls.object.position.copy(newPosition);
                APP.controls.target.copy(newTarget);
              } else {
                // Keine Begrenzung wenn noch keine Objekte platziert wurden
                APP.controls.object.position.add(delta);
                APP.controls.target.add(delta);
              }
              APP.controls.resetFromObjectAndTarget();
            };
          }
        }
      }
    }
    if (verschiebenFunctionRef.current) {
      verschiebenFunctionRef.current(pointerState);
    }
    if (pointerState === "up") {
      verschiebenFunctionRef.current = null;
    }
  };

  const zeichnen = (pointerState: string) => {
    if (!cs) return;
    const grundriss = APP.arrangement.grundriss;
    const start = snapFunctionRef.current(cs.pointToGrundriss(moveStartRef.current, 0));
    const end = snapFunctionRef.current(cs.pointToGrundriss(moveEndRef.current, 0));
    maybeShowVerschiebenSnappToast();
    if (neueKanteRef.current == null && moveEndRef.current?.distanceTo(moveStartRef.current) > 5) {
      const ecken = [start, end]
        .map((p) => Maths3.v3To2xz(p))
        .map((p) => createEcke(Commons.createId(), p)) as [GrundrissEcke, GrundrissEcke];
      const kante = createKante(...ecken);
      grundriss.ecken.push(...ecken);
      ecken.forEach((ecke) => ecke.anliegendeKanten.push(kante));
      grundriss.kanten.push(kante);
      neueKanteRef.current = kante;
    }
    if (neueKanteRef.current) {
      const kante = neueKanteRef.current;
      kante.ecken[1].punkt.copy(Maths3.v3To2xz(end));
    }
    if (pointerState === "up") {
      if (neueKanteRef.current) {
        const strecke = neueKanteRef.current.ecken.map((e) => e.punkt) as Strecke2;
        const distance = Maths2.distance(strecke);
        if (distance > 0.01) {
          APP.logUserAction({ action: "zeichnen" }, { cached: true });
        }
      }
      grundriss.ecken = kantenZuEcken(grundriss.kanten);
      neueKanteRef.current = null;
    }
  };

  const radieren = (pointerState: string) => {
    if (!cs) return;
    const grundriss = APP.arrangement.grundriss;
    const start = Maths3.v3To2xz(cs.pointToGrundriss(moveStartRef.current, 0));
    const end = Maths3.v3To2xz(cs.pointToGrundriss(moveEndRef.current, 0));
    const radius = cs.lengthToGrundriss(32);
    removeFromStrecken(grundriss.ecken, grundriss.kanten, {
      line: [start, end],
      radius,
    });
    moveStartRef.current = moveEndRef.current;
  };

  const selektieren = (pointerState: string) => {
    if (!cs) return;
    if (selectionRectangleRef.current) {
      const style = selectionRectangleRef.current.style;
      style.display = pointerState === "up" ? "none" : "block";
      if (moveStartRef.current && moveEndRef.current) {
        style.left = Math.min(moveStartRef.current.x, moveEndRef.current.x) + "px";
        style.top = Math.min(moveStartRef.current.y, moveEndRef.current.y) + "px";
        style.width = Math.abs(moveEndRef.current.x - moveStartRef.current.x) + "px";
        style.height = Math.abs(moveEndRef.current.y - moveStartRef.current.y) + "px";
      }
    }
    if (pointerState === "up") {
      const start = Maths3.v3To2xz(cs.pointToGrundriss(moveStartRef.current, 0));
      const end = Maths3.v3To2xz(cs.pointToGrundriss(moveEndRef.current, 0));
      const selectBox2 = new Box2().setFromPoints([start, end]);
      const aufbauten = APP.arrangement.aufbauten;
      const parts = APP.arrangement.getParts();
      const selection = [...parts, ...aufbauten].filter((o) => {
        const { min, max } = o.getBox() as any as { min: Vector3; max: Vector3 };
        const points = [min, max].map((p) => Maths3.v3To2xz(o.localToWorld(p)));
        return points.every((p) => selectBox2.containsPoint(p));
      });
      setSelected(selection);
    }
  };

  const handlePointerMoveModus = (e: React.PointerEvent, pointerState: string) => {
    if (isPinchingRef.current) {
      // If Pinch Gesture is active, do not perform mode-based actions
      return;
    }

    if (!moveStartRef.current) {
      return;
    }
    moveEndRef.current = v2(e.clientX, e.clientY);
    if (state.modus === "verschieben") {
      verschieben(pointerState);
    } else if (state.modus === "zeichnen") {
      zeichnen(pointerState);
    } else if (state.modus === "radieren") {
      radieren(pointerState);
    } else if (state.modus === "selektieren") {
      selektieren(pointerState);
    }
    forceUpdate();
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!isStage(e)) {
      // Prevent interference with Aufbauten interactions
      return;
    }

    // Prevent more than two simultaneous pointers
    if (activePointersRef.current.size >= 2) return;

    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    activePointersRef.current.set(e.pointerId, v2(e.clientX, e.clientY));
    const points = Array.from(activePointersRef.current.values()) as Strecke2;
    initialTouchPointsRef.current = points;
    initialCameraRef.current = APP.controls.object.position.clone();

    if (activePointersRef.current.size === 1) {
      // Single finger interaction: Prepare for drawing or moving
      pointerDownStartRef.current = e.timeStamp;
      moveStartRef.current = v2(e.clientX, e.clientY);
      moveEndRef.current = v2(e.clientX, e.clientY);
      isDragRef.current = false;

      const start = cs ? cs.pointToGrundriss(moveStartRef.current, 0) : null;
      const startV2 = start ? Maths3.v3To2xz(start) : null;

      if (e.button === 2) {
        modusBeforeRef.current = state.modus;
        setNewState({ modus: "verschieben" });
      } else {
        const tor = startV2 ? findTorAt(startV2, cs) : null;
        if (tor) {
          if (state.modus === "radieren" || e.button === 1) {
            // Radieren on a gate should delete the gate and prevent further actions
            tor.uiRemove();
            moveStartRef.current = null;
            modusBeforeRef.current = state.modus;
            setNewState({ modus: "verschieben" });
            return;
          } else if (state.modus !== "selektieren" || e.button === 2) {
            startVerschiebeTor(startV2, tor);
            modusBeforeRef.current = state.modus;
            setNewState({ modus: "verschieben" });
            return;
          }
        } else if (e.button === 1) {
          modusBeforeRef.current = state.modus;
          setNewState({ modus: "radieren" });
        }
      }

      snapFunctionRef.current = createSnapFunction();
      handlePointerMoveModus(e, "down");
      setPointerDown(true);
    } else if (activePointersRef.current.size === 2) {
      // Two fingers: Start Pinch-Zoom and Pan Gesture
      isPinchingRef.current = true;
      // Set the flag indicating a pinch gesture was performed
      gestureWasPinchRef.current = true;
      modusBeforeRef.current = state.modus;

      setNewState({ modus: "zoom" });

      // Reset Drawing State to Prevent Unintended Drawing
      moveStartRef.current = null;
      moveEndRef.current = null;

      return; // Prevent further processing
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!activePointersRef.current.has(e.pointerId)) return;

    const newPos = v2(e.clientX, e.clientY);
    activePointersRef.current.set(e.pointerId, newPos);

    if (activePointersRef.current.size === 1 && !isPinchingRef.current) {
      // Single finger movement: Handle drawing or moving
      moveEndRef.current = newPos;
      handlePointerMoveModus(e, "move");
    } else if (activePointersRef.current.size === 2 && isPinchingRef.current) {
      // Two fingers: Handle Pinch Zoom and Pan
      const points = Array.from(activePointersRef.current.values()) as Strecke2;
      const initialPoints = initialTouchPointsRef.current;

      const centroidDelta = Maths2.middle(points).sub(Maths2.middle(initialPoints));
      const distanceRatio = Maths2.distance(points) / Maths2.distance(initialPoints);

      const initialCameraPos = initialCameraRef.current;

      let newCameraY = initialCameraPos.y / distanceRatio;

      // Clamp the newCameraY to sensible values
      const minZoomY = 1;
      const maxZoomY = 100;
      newCameraY = Math.min(Math.max(newCameraY, minZoomY), maxZoomY);

      let newCameraPos = initialCameraPos
        .clone()
        .sub(cs.sizeToGrundriss(Maths3.v2To3xy(centroidDelta), 0))
        .setY(newCameraY);

      // Begrenzung der Viewport-Position wenn Objekte vorhanden sind
      const grundriss = APP.arrangement.grundriss;
      if (grundriss.ecken.length > 0 || APP.arrangement.aufbauten.length > 0) {
        const puffer = 10; // 10 Meter Puffer um den Grundriss
        const boundingBox = grundriss.boundingBox.clone();
        boundingBox.expandByScalar(puffer);

        // Begrenze die X- und Z-Koordinaten auf die erweiterte BoundingBox
        newCameraPos.x = Math.max(boundingBox.min.x, Math.min(boundingBox.max.x, newCameraPos.x));
        newCameraPos.z = Math.max(boundingBox.min.z, Math.min(boundingBox.max.z, newCameraPos.z));
      }

      cs.setCameraTo(newCameraPos);
    }

    forceUpdate();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!activePointersRef.current.has(e.pointerId)) return;

    activePointersRef.current.delete(e.pointerId);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);

    if (isPinchingRef.current && activePointersRef.current.size < 2) {
      // Pinch Gesture End
      isPinchingRef.current = false;
      setNewState({ modus: modusBeforeRef.current || "zeichnen" });
      modusBeforeRef.current = state.modus;
      APP.logUserAction({ action: "pinch" }, { cached: true, stateChange: false });
      return; // Prevent further actions (like drawing)
    }

    if (activePointersRef.current.size === 1 && isPinchingRef.current) {
      // One pointer remains active, but pinch is ended
      const remainingPointerId = Array.from(activePointersRef.current.keys())[0];
      const remainingPos = activePointersRef.current.get(remainingPointerId);
      moveStartRef.current = remainingPos ? remainingPos.clone() : null;
      moveEndRef.current = remainingPos ? remainingPos.clone() : null;
    }

    if (activePointersRef.current.size === 0 && !isPinchingRef.current) {
      // Check if the last gesture was a pinch
      if (gestureWasPinchRef.current) {
        // Reset the flag and skip drawing actions
        gestureWasPinchRef.current = false;
        return; // Do not call handlePointerMoveModus to prevent drawing
      }

      // End of single-pointer interaction
      isDragRef.current =
        moveStartRef.current &&
        pointerDownStartRef.current !== null &&
        (v2(e.clientX, e.clientY).distanceTo(moveStartRef.current) > 10 ||
          e.timeStamp - pointerDownStartRef.current > 200);
      handlePointerMoveModus(e, "up");
      setPointerDown(false);
      pointerDownStartRef.current = null;
      moveStartRef.current = null;
      moveEndRef.current = null;
      APP.dirty();

      if (modusBeforeRef.current) {
        setNewState({ modus: modusBeforeRef.current });
        modusBeforeRef.current = null;
      }
      APP.undoHandler.save();
      snapFunctionRef.current = null;
      verschiebenTargetRef.current = null;
      verschiebenFunctionRef.current = null;
    }

    forceUpdate();
  };

  const handleContextMenu = (e: React.MouseEvent) => e.preventDefault();

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleWheel,
    handleClick,
    handleContextMenu,
  };
}

function findClosestGridLinePoint(
  rays: Ray2[],
  widerSnapDistance: number,
  snapDistance: number,
  point: Vector2
) {
  const raySnaps = rays
    .map((r) => [r, r.distanceToPoint(point)] as [Ray2, number])
    .filter(([r, d]) => d < widerSnapDistance);
  if (raySnaps.length > 0) {
    const [ray, distance] = _.minBy(raySnaps, ([r, d]) => d);
    const s = ray.closestPointToPoint(point);
    if (KeyState.isCtrl()) {
      return s;
    }
    const gmin = (n) => Math.floor(n / snapDistance) * snapDistance;
    const gmax = (n) => (Math.floor(n / snapDistance) + 1) * snapDistance;
    const xs = [gmin(point.x), gmax(point.x)];
    const ys = [gmin(point.y), gmax(point.y)];
    const gridLines = [
      [v2(xs[0], ys[0]), v2(xs[1], ys[0])],
      [v2(xs[1], ys[0]), v2(xs[1], ys[1])],
      [v2(xs[1], ys[1]), v2(xs[0], ys[1])],
      [v2(xs[0], ys[1]), v2(xs[0], ys[0])],
    ];
    const rl = [ray.origin, ray.origin.clone().add(ray.direction)];
    const intersections = gridLines
      .map((gl): Vector2 => Maths2.lineInfiniteIntersection(gl, rl))
      .map((intersection): [Vector2, number] => [
        intersection,
        intersection == null ? Number.POSITIVE_INFINITY : intersection.distanceTo(point),
      ])
      .filter(([intersection, distance]) => distance < widerSnapDistance);
    if (intersections.length > 0) {
      const [intersection, distance] = _.minBy(
        intersections,
        ([intersection, distance]) => distance
      );
      return intersection;
    }
  }
  return null;
}

function isStage(event: React.SyntheticEvent | Event) {
  const target = event.target as HTMLElement;
  return target.classList.contains("stage2d");
}

function getClickedPart(e: React.MouseEvent | React.PointerEvent, cs: CoordinateSystem) {
  if (!cs) return null;
  const point = v2(e.clientX, e.clientY);
  const gpoint = Maths3.v3To2xz(cs.pointToGrundriss(point, 0));
  const gkanteToPoints = (gkante) => gkante.ecken.map((gecke) => gecke.punkt);
  const [gkante, distance] =
    _.minBy(
      APP.arrangement.grundriss.kanten.map((gkante) => [
        gkante,
        distanceToLineSegment(gpoint, gkanteToPoints(gkante)),
      ]),
      ([_, distance]) => distance
    ) ?? [];
  if (gkante == null) {
    return null;
  }
  const distpx = cs.lengthToScreen(distance);
  if (distpx < 10) {
    const segment = APP.arrangement.segmente.find((s) => s.segmentKante.grundrissKante === gkante);
    if (segment) {
      const x = new Ray2()
        .setFromPoints(gkanteToPoints(gkante))
        .distanceOriginToPointProjection(gpoint);
      return segment.getPartOrPfostenAtPositionX(x);
    }
  }
  return null;
}
